import { FC, Fragment, useState } from "react";
import {
  Box,
  Checkbox,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { useIntl } from "react-intl";

import { SemaforIcon } from "../../../../../utils";
import { IEventLog, MESSAGES_HEADERS_ID } from "../table";
import {
  StyledBodyTableCell,
  StyledHeaderTableCell,
  StyledParentBodyTableCell,
  StyledParentHeaderCollapseTableCell,
} from "./styled";

interface IRow {
  row: IEventLog;
  setAckEventId: (id: number | null) => void;
  setAckDialogOpen: (open: boolean) => void;
}

export const Row: FC<IRow> = props => {
  const { row, setAckEventId, setAckDialogOpen } = props;
  const [open, setOpen] = useState(false);

  const { formatMessage } = useIntl();

  return (
    <Fragment>
      <TableRow sx={{ "& > *": { borderBottom: "0px", borderRight: "1px solid #EBEBF7" } }}>
        <StyledParentHeaderCollapseTableCell align="center">
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </StyledParentHeaderCollapseTableCell>
        <StyledParentBodyTableCell component="th" scope="row" sx={{ width: "24%" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <SemaforIcon status={row.status} />
          </Box>
        </StyledParentBodyTableCell>
        <StyledParentBodyTableCell component="th" scope="row" sx={{ width: "24%" }}>
          <Typography fontSize={15} fontWeight={500} lineHeight="16px" color="#363846">
            {row.position}
          </Typography>
        </StyledParentBodyTableCell>
        <StyledParentBodyTableCell component="th" scope="row" sx={{ width: "24%" }}>
          <Typography fontSize={15} fontWeight={500} lineHeight="16px" color="#363846">
            {row.rank}
          </Typography>
        </StyledParentBodyTableCell>
        <StyledParentBodyTableCell component="th" scope="row" sx={{ width: "24%" }}>
          <Typography fontSize={15} fontWeight={500} lineHeight="16px" color="#363846">
            {row.date}
          </Typography>
        </StyledParentBodyTableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ padding: 0 }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 0, marginLeft: "4%", borderLeft: "1px solid #EBEBF7" }}>
              <Table aria-label="components" sx={{ tableLayout: "fixed" }}>
                <TableHead>
                  <TableRow sx={{ "& > *": { background: "#FAFAFF", borderRight: "1px solid #EBEBF7" } }}>
                    <StyledHeaderTableCell component="th" scope="row" sx={{ width: "12%" }}>
                      <Typography fontSize={15} fontWeight={500} lineHeight="16px" color="#363846">
                        {formatMessage({ id: `${MESSAGES_HEADERS_ID}.ackStatus` })}
                      </Typography>
                    </StyledHeaderTableCell>
                    <StyledHeaderTableCell component="th" scope="row" sx={{ width: "7%" }}>
                      <Typography fontSize={15} fontWeight={500} lineHeight="16px" color="#363846">
                        {formatMessage({ id: `${MESSAGES_HEADERS_ID}.status` })}
                      </Typography>
                    </StyledHeaderTableCell>
                    <StyledHeaderTableCell component="th" scope="row" sx={{ width: "11%" }}>
                      <Typography fontSize={15} fontWeight={500} lineHeight="16px" color="#363846">
                        {formatMessage({ id: `${MESSAGES_HEADERS_ID}.eventType` })}
                      </Typography>
                    </StyledHeaderTableCell>
                    <StyledHeaderTableCell component="th" scope="row" sx={{ width: "7%" }}>
                      <Typography fontSize={15} fontWeight={500} lineHeight="16px" color="#363846">
                        {formatMessage({ id: `${MESSAGES_HEADERS_ID}.point` })}
                      </Typography>
                    </StyledHeaderTableCell>
                    <StyledHeaderTableCell component="th" scope="row" sx={{ width: "9%" }}>
                      <Typography fontSize={15} fontWeight={500} lineHeight="16px" color="#363846">
                        {formatMessage({ id: `${MESSAGES_HEADERS_ID}.dateTime` })}
                      </Typography>
                    </StyledHeaderTableCell>
                    <StyledHeaderTableCell component="th" scope="row" sx={{ width: "10%" }}>
                      <Typography fontSize={15} fontWeight={500} lineHeight="16px" color="#363846">
                        {formatMessage({ id: `${MESSAGES_HEADERS_ID}.parameter` })}
                      </Typography>
                    </StyledHeaderTableCell>
                    <StyledHeaderTableCell component="th" scope="row" sx={{ width: "9%" }}>
                      <Typography fontSize={15} fontWeight={500} lineHeight="16px" color="#363846">
                        {formatMessage({ id: `${MESSAGES_HEADERS_ID}.source` })}
                      </Typography>
                    </StyledHeaderTableCell>
                    <StyledHeaderTableCell component="th" scope="row" sx={{ width: "9%" }}>
                      <Typography fontSize={15} fontWeight={500} lineHeight="16px" color="#363846">
                        {formatMessage({ id: `${MESSAGES_HEADERS_ID}.fullName` })}
                      </Typography>
                    </StyledHeaderTableCell>
                    <StyledHeaderTableCell component="th" scope="row" sx={{ width: "12%" }}>
                      <Typography fontSize={15} fontWeight={500} lineHeight="16px" color="#363846">
                        {formatMessage({ id: `${MESSAGES_HEADERS_ID}.message` })}
                      </Typography>
                    </StyledHeaderTableCell>
                    <StyledHeaderTableCell component="th" scope="row" sx={{ width: "10%" }}>
                      <Typography fontSize={15} fontWeight={500} lineHeight="16px" color="#363846">
                        {formatMessage({ id: `${MESSAGES_HEADERS_ID}.defect` })}
                      </Typography>
                    </StyledHeaderTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.messages.map(
                    ({
                      idEvent,
                      ackStatus,
                      status,
                      type,
                      pointName,
                      date,
                      parameter,
                      source,
                      fullName,
                      message,
                      defect,
                    }) => (
                      <TableRow key={idEvent} sx={{ "& > *": { borderRight: "1px solid #EBEBF7" } }}>
                        <StyledBodyTableCell sx={{ width: "12%" }}>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Checkbox
                              checked={ackStatus === 1 || ackStatus === 2}
                              disabled={ackStatus !== 0}
                              onChange={() => {
                                if (ackStatus === 0) {
                                  setAckEventId(idEvent);
                                  setAckDialogOpen(true);
                                }
                              }}
                              size="small"
                            />
                          </Box>
                        </StyledBodyTableCell>
                        <StyledBodyTableCell sx={{ width: "7%" }}>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <SemaforIcon status={status} />
                          </Box>
                        </StyledBodyTableCell>
                        <StyledBodyTableCell sx={{ width: "11%" }}>{type}</StyledBodyTableCell>
                        <StyledBodyTableCell sx={{ width: "7%" }}>{pointName}</StyledBodyTableCell>
                        <StyledBodyTableCell sx={{ width: "9%" }}>{date}</StyledBodyTableCell>
                        <StyledBodyTableCell sx={{ width: "10%" }}>{parameter}</StyledBodyTableCell>
                        <StyledBodyTableCell sx={{ width: "9%" }}>{source}</StyledBodyTableCell>
                        <StyledBodyTableCell sx={{ width: "9%" }}>{fullName}</StyledBodyTableCell>
                        <StyledBodyTableCell sx={{ width: "12%" }}>{message}</StyledBodyTableCell>
                        <StyledBodyTableCell sx={{ width: "10%" }}>{defect}</StyledBodyTableCell>
                      </TableRow>
                    ),
                  )}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};
